package dev.youndie.site.pages.blog

import androidx.compose.runtime.*
import com.varabyte.kobweb.core.Page
import com.varabyte.kobweb.core.data.*
import com.varabyte.kobweb.core.layout.Layout
import com.varabyte.kobweb.core.layout.NoLayout
import com.varabyte.kobweb.core.init.InitRoute
import com.varabyte.kobweb.core.init.InitRouteContext
import com.varabyte.kobwebx.frontmatter.*
import com.varabyte.kobwebx.markdown.*

@InitRoute
fun initWhatIsLeftIfYouDieHalfwayPage(ctx: InitRouteContext) {
    ctx.data.add(MarkdownContext("blog/WhatIsLeftIfYouDieHalfway.md", FrontMatterElement.Builder { addScalar("title", "What is left in the system if you die halfway"); addScalar("searchTitle", "Distributed saga engine for Kotlin with compensation and an outbox"); addScalar("date", "2026-04-12"); addScalar("description", "A saga engine built around one question. Steps as interceptors, compensation in reverse, and an outbox that makes \"the work happened but the notification never went out\" structurally impossible."); addScalar("library", "petich"); }))
}

@Page("/blog/what-is-left-if-you-die-halfway")
@Layout(".components.layouts.MarkdownLayout")
@Composable
fun WhatIsLeftIfYouDieHalfwayPage() {
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("An operation that spans several services is not one database write. Reserve capacity, claim a quota, apply the change, hand it to a downstream system, notify. Any step can refuse, and by then some of the earlier ones are already irreversible.")
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("try/catch")
        }
        org.jetbrains.compose.web.dom.Text(" does not help here. What needs undoing is not a transaction but ")
        org.jetbrains.compose.web.dom.Em {
            org.jetbrains.compose.web.dom.Text("actions that already happened")
        }
        org.jetbrains.compose.web.dom.Text(", in reverse order, and only the ones that really did.")
    }
    org.jetbrains.compose.web.dom.P {
        com.varabyte.kobweb.silk.components.navigation.Link("https://github.com/youndie/petich") {
            org.jetbrains.compose.web.dom.Text("petich")
        }
        org.jetbrains.compose.web.dom.Text(" is built around that one question: what is left in the system if you die halfway.")
    }
    org.jetbrains.compose.web.dom.H2(attrs = { id("one-interceptor-one-step-forward-and-one-step-back") }) {
        org.jetbrains.compose.web.dom.Text("One interceptor, one step forward and one step back")
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("A step declares both directions:")
    }
    org.jetbrains.compose.web.dom.Pre { org.jetbrains.compose.web.dom.Code(attrs = { classes("language-text") }) { org.jetbrains.compose.web.dom.Text("""execute()    — do the thing
compensate() — undo the thing you did
""") } }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("Steps run in phases — ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("ENRICHMENT → VALIDATION → AUTHORIZATION → EXECUTION → POST_PROCESSING")
        }
        org.jetbrains.compose.web.dom.Text(" — ordered by priority within a phase. A failure at step N calls ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("compensate()")
        }
        org.jetbrains.compose.web.dom.Text(" on N−1 … 1, in reverse.")
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("Putting both halves in one object is the point. When undo lives somewhere else, it drifts from do, and it drifts silently: nobody runs the compensation path on the happy day.")
    }
    org.jetbrains.compose.web.dom.H2(attrs = { id("waiting-for-a-human-without-holding-anything") }) {
        org.jetbrains.compose.web.dom.Text("Waiting for a human without holding anything")
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("Some sagas stop and wait for a person to confirm. The naive implementation holds a thread, or a database connection, or both, for however long that takes.")
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("petich suspends: the saga is a value, it goes to storage, and it resumes on a later HTTP request. Nothing is held in the meantime.")
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("Which immediately raises the real question — what about the one nobody comes back to? A suspended saga has already claimed resources. Left alone it holds them forever. So the wait has a deadline, and a background sweeper rolls back what expired.")
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("That pair matters more than the suspend itself. Suspension without a deadline is a resource leak with better manners.")
    }
    org.jetbrains.compose.web.dom.H2(attrs = { id("the-outbox-is-the-interesting-part") }) {
        org.jetbrains.compose.web.dom.Text("The outbox is the interesting part")
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("The classic distributed failure is not a crash. It is this: the work committed, and the event announcing it did not. Downstream never finds out, and no error was raised anywhere.")
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("With an outbox-aware repository — ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("petich-postgres")
        }
        org.jetbrains.compose.web.dom.Text(" is one — the ")
        org.jetbrains.compose.web.dom.Em {
            org.jetbrains.compose.web.dom.Text("intent to emit")
        }
        org.jetbrains.compose.web.dom.Text(" is written in the same transaction as the state change. Either both land or neither does. There is no window.")
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("A repository without that support still works. The engine falls back to a plain update and drops the events, which is a deliberate choice: degrade visibly to \"no events\" rather than pretend to a guarantee the storage cannot make.")
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("Races are handled by optimistic locking on a version, plus a per-saga mutex inside the process.")
    }
    org.jetbrains.compose.web.dom.H2(attrs = { id("three-modules-that-do-not-depend-on-the-core") }) {
        org.jetbrains.compose.web.dom.Text("Three modules that do not depend on the core")
    }
    org.jetbrains.compose.web.dom.Table {
        org.jetbrains.compose.web.dom.Thead {
            org.jetbrains.compose.web.dom.Tr {
                org.jetbrains.compose.web.dom.Th {
                    org.jetbrains.compose.web.dom.Text("module")
                }
                org.jetbrains.compose.web.dom.Th {
                    org.jetbrains.compose.web.dom.Text("what for")
                }
            }
        }
        org.jetbrains.compose.web.dom.Tbody {
            org.jetbrains.compose.web.dom.Tr {
                org.jetbrains.compose.web.dom.Td {
                    org.jetbrains.compose.web.dom.Code {
                        org.jetbrains.compose.web.dom.Text("petich-core")
                    }
                }
                org.jetbrains.compose.web.dom.Td {
                    org.jetbrains.compose.web.dom.Text("the engine: sagas, interceptors, phases, compensation, suspend/resume, TTL")
                }
            }
            org.jetbrains.compose.web.dom.Tr {
                org.jetbrains.compose.web.dom.Td {
                    org.jetbrains.compose.web.dom.Code {
                        org.jetbrains.compose.web.dom.Text("petich-ktor")
                    }
                }
                org.jetbrains.compose.web.dom.Td {
                    org.jetbrains.compose.web.dom.Text("REST endpoints for creating and resuming a saga")
                }
            }
            org.jetbrains.compose.web.dom.Tr {
                org.jetbrains.compose.web.dom.Td {
                    org.jetbrains.compose.web.dom.Code {
                        org.jetbrains.compose.web.dom.Text("petich-postgres")
                    }
                }
                org.jetbrains.compose.web.dom.Td {
                    org.jetbrains.compose.web.dom.Text("storage on Exposed — outbox, idempotency, scheduler")
                }
            }
            org.jetbrains.compose.web.dom.Tr {
                org.jetbrains.compose.web.dom.Td {
                    org.jetbrains.compose.web.dom.Code {
                        org.jetbrains.compose.web.dom.Text("petich-outbox-core")
                    }
                }
                org.jetbrains.compose.web.dom.Td {
                    org.jetbrains.compose.web.dom.Text("at-least-once delivery with backoff and dead lettering")
                }
            }
            org.jetbrains.compose.web.dom.Tr {
                org.jetbrains.compose.web.dom.Td {
                    org.jetbrains.compose.web.dom.Code {
                        org.jetbrains.compose.web.dom.Text("petich-idempotency")
                    }
                }
                org.jetbrains.compose.web.dom.Td {
                    org.jetbrains.compose.web.dom.Text("protection against a key reused with a ")
                    org.jetbrains.compose.web.dom.Em {
                        org.jetbrains.compose.web.dom.Text("different")
                    }
                    org.jetbrains.compose.web.dom.Text(" request")
                }
            }
            org.jetbrains.compose.web.dom.Tr {
                org.jetbrains.compose.web.dom.Td {
                    org.jetbrains.compose.web.dom.Code {
                        org.jetbrains.compose.web.dom.Text("petich-scheduler")
                    }
                }
                org.jetbrains.compose.web.dom.Td {
                    org.jetbrains.compose.web.dom.Text("a saga on a schedule, with no HTTP initiator")
                }
            }
        }
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("The last three know nothing about sagas. ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("petich-outbox-core")
        }
        org.jetbrains.compose.web.dom.Text(" knows only about a row — id, type, payload, deliver at least once. ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("petich-scheduler")
        }
        org.jetbrains.compose.web.dom.Text(" knows \"it is time\" and \"here is the payload\". ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("petich-idempotency")
        }
        org.jetbrains.compose.web.dom.Text(" knows \"this key already arrived with a different fingerprint\".")
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("That independence is not tidiness. It is the condition under which they stay usable on their own instead of turning into part of somebody's feature.")
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("petich-postgres")
        }
        org.jetbrains.compose.web.dom.Text(" also ships no driver and no connection pool. It takes an Exposed ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("Database")
        }
        org.jetbrains.compose.web.dom.Text(" and does not care what is underneath it — choosing a driver is the application's decision, not a library's.")
    }
    com.varabyte.kobweb.silk.components.layout.HorizontalDivider()
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("Source: ")
        com.varabyte.kobweb.silk.components.navigation.Link("https://github.com/youndie/petich") {
            org.jetbrains.compose.web.dom.Text("github.com/youndie/petich")
        }
    }
}
